import styled from '@emotion/styled';
import { Delete, Upload } from '@mui/icons-material';
import { Box, IconButton, Typography, LinearProgress } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { GetPresignedUrl, UploadImageToS3 } from '../../services/service';

export const ImageUpload = ({
  imageUrl: initialImageUrl,
  onChange,
  onRemove,
  uploadLabel,
  fileId,
  type,
  disabled,
  setLoader,
  folderType,
  variant = 'default',
  aspectRatio = 16 / 9,
}: {
  imageUrl?: string | null | undefined;
  onChange?: (url: string) => void;
  onRemove?: () => void;
  uploadLabel?: string;
  fileId: string;
  type: 'icon' | 'banner' | 'profile';
  disabled: boolean;
  setLoader?: Dispatch<SetStateAction<boolean>>;
  folderType: number;
  // 'tile' is an opt-in, more visual style (full-bleed image, gradient
  // hover overlay, rounded media-card look) for banner-management screens
  // that show many previews at once. 'default' is the original compact
  // dashed-box look every other caller already uses - unchanged.
  variant?: 'default' | 'tile';
  aspectRatio?: number;
}) => {
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [localImagePreview, setLocalImagePreview] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setImageUrl(initialImageUrl || '');
  }, [initialImageUrl]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setLoader) {
      setLoader(true);
    }
    const file = e.target.files ? e.target.files[0] : null;
    if (file && ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      const reader = new FileReader();
      reader.onload = () => {
        const preview = reader.result as string;
        setLocalImagePreview(preview);
      };

      reader.readAsDataURL(file);

      setIsUploading(true);
      try {
        const FileName = file.name.split('.');
        const FileType = file.type.split('/');

        const presignedUrl = await GetPresignedUrl(
          `${FileName[0]}.${FileType[1]}`,
          file.type,
          folderType,
        );

        const uploadedImageUrl = await UploadImageToS3(file, presignedUrl);
        setImageUrl(uploadedImageUrl);
        onChange?.(uploadedImageUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
      } finally {
        setIsUploading(false);
        setLocalImagePreview(null);
        if (setLoader) {
          setLoader(false);
        }
      }
    }
  };

  const handleRemoveFile = () => {
    setImageUrl('');
    onRemove?.();
  };

  const ImageBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isDashed',
  })<{ isDashed?: boolean }>(({ isDashed }) => ({
    border: isDashed ? '1px dashed #ddd' : '1px solid #ddd',
    borderRadius: 8,
    width: type === 'icon' ? '50%' : '100%',
    height: type === 'icon' ? 120 : 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: 16,
    cursor: 'pointer',
  }));

  const UploadIconBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    cursor: 'pointer',
    color: '#999',
  });

  const ImagePreview = styled('img')({
    width: type === 'icon' ? '50%' : '80%',
    height: type === 'icon' ? '80%' : '90%',
    objectFit: 'contain',
  });

  const LocalImagePreview = styled('img')({
    width: type === 'icon' ? '50%' : '80%',
    height: type === 'icon' ? '80%' : '90%',
    objectFit: 'contain',
    opacity: 0.5, // To show the preview with some transparency
  });

  if (isUploading && localImagePreview) {
    console.log('PREVIEW TIME');
  } else if (imageUrl) {
    console.log('IMAGE URL Time');
  } else {
    console.log('UPLOAD TIME');
  }

  if (variant === 'tile') {
    const hasImage = Boolean(imageUrl) && !isUploading;
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: String(aspectRatio),
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: '#171717',
          border: hasImage ? '1px solid rgba(255,255,255,0.08)' : '1px dashed rgba(123,31,162,0.45)',
          background: hasImage
            ? undefined
            : 'linear-gradient(135deg, rgba(123,31,162,0.16), rgba(123,31,162,0.03))',
          transition: 'border-color .18s ease, box-shadow .18s ease, transform .18s ease',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: 'rgba(123,31,162,0.8)',
                boxShadow: '0 10px 28px rgba(123,31,162,0.28)',
                transform: 'translateY(-2px)',
                '& .tile-hover-overlay': { opacity: 1 },
              },
        }}
      >
        {isUploading ? (
          <>
            {localImagePreview && (
              <img
                src={localImagePreview}
                alt="Uploading…"
                style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }}
              />
            )}
            <LinearProgress
              sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
            />
          </>
        ) : hasImage ? (
          <>
            <img
              src={imageUrl as string}
              alt={uploadLabel || 'Banner'}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box
              className="tile-hover-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.78) 100%)',
                opacity: 0,
                transition: 'opacity .18s ease',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                p: 1,
              }}
            >
              <Box
                component="label"
                htmlFor={fileId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(6px)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 5,
                  cursor: disabled ? 'default' : 'pointer',
                }}
              >
                <Upload sx={{ fontSize: 14, color: '#fff' }} />
                <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                  Change
                </Typography>
              </Box>
              {!disabled && (
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.45)',
                    '&:hover': { bgcolor: 'rgba(227,30,37,0.75)' },
                  }}
                >
                  <Delete sx={{ fontSize: 14, color: '#fff' }} />
                </IconButton>
              )}
            </Box>
          </>
        ) : (
          <Box
            component="label"
            htmlFor={fileId}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(123,31,162,0.2)',
              }}
            >
              <Upload sx={{ fontSize: 17, color: '#B36FD1' }} />
            </Box>
            <Typography sx={{ color: '#8A8D91', fontSize: 12 }}>
              {uploadLabel || 'Upload'}
            </Typography>
          </Box>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id={fileId}
        />
      </Box>
    );
  }

  return (
    <ImageBox isDashed={!imageUrl && !isUploading && !localImagePreview}>
      {isUploading ? (
        localImagePreview ? (
          <>
            <LocalImagePreview src={localImagePreview} alt="Uploading..." />
            <LinearProgress
              style={{ position: 'absolute', bottom: 0, width: '100%' }}
            />
          </>
        ) : (
          <Typography>Loading...</Typography>
        )
      ) : imageUrl ? (
        <ImagePreview src={imageUrl} alt="Image Preview" />
      ) : (
        <label htmlFor={fileId}>
          <UploadIconBox>
            <Upload fontSize="large" />
            <Typography variant="body2" style={{ marginLeft: 8 }}>
              {uploadLabel}
            </Typography>
          </UploadIconBox>
        </label>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/jpg"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id={fileId}
      />
      {!disabled && imageUrl && (
        <IconButton
          onClick={handleRemoveFile}
          style={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Delete fontSize="small" />
        </IconButton>
      )}
    </ImageBox>
  );
};
